import * as Sentry from '@sentry/node';
import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { Duration } from 'luxon';
import { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import SVG from 'react-inlinesvg';
import Gataware from '../../SDKs/Gataware';
import AboutUsDefaultContent from '../../components/AboutUsDefaultContent';
import CtaButtons from '../../components/CtaButtons';
import Layout from '../../components/Layout';
import OpenGraphDescription from '../../components/OpenGraphDescription';
import OpenGraphTitle from '../../components/OpenGraphTitle';
import PageTitle from '../../components/PageTitle';
import Section from '../../components/Section';
import Head from '../../components/head';
import { FranchiseeProvider } from '../../context/Franchisee';
import {
  AboutUsData,
  ContactUsData,
  FranchiseeState,
} from '../../context/Franchisee/franchisee.types';

export const getStaticPaths: GetStaticPaths = async () => {
  const popularStores: Array<string> = ['fort-worth'];
  const paths = popularStores.map((storeSlug) => ({
    params: { franchiseeSlug: storeSlug },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { franchiseeSlug } = params;

  const response = await Gataware.getLocations({
    slugs: [franchiseeSlug.toLocaleString()],
  }).then(({ data }) => JSON.parse(data));

  const franchisees = await response.data
    .filter(({ slug }) => franchiseeSlug === slug)
    .map((franchisee) => camelcaseKeys(franchisee));

  if (!franchisees.length) {
    return {
      notFound: true,
      revalidate: 28800,
    };
  }

  const [franchisee] = franchisees;

  let { aboutUsData, contactUsData } = franchisee;
  if (aboutUsData !== null && Object.keys(aboutUsData).length) {
    aboutUsData = camelcaseKeys(aboutUsData);
  }
  if (contactUsData !== null && Object.keys(contactUsData).length) {
    contactUsData = camelcaseKeys(contactUsData);
  }

  return {
    props: {
      franchisee,
      aboutUsData: aboutUsData || null,
      contactUsData: contactUsData || null,
    },
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

interface AboutPageProps {
  /* add about page props here */
  franchisee: FranchiseeState;
  aboutUsData?: AboutUsData;
  contactUsData?: ContactUsData;
}

const About: React.FC<AboutPageProps> = ({
  franchisee,
  aboutUsData,
  contactUsData,
}: AboutPageProps) => {
  return (
    <FranchiseeProvider franchiseeValue={franchisee}>
      <Layout alternateFooter={true}>
        <Head
          slug='about'
          title={`About | Two Maids | ${franchisee.name}`}
          description={`About Two Maids  ${franchisee.name}`}
        />
        <OpenGraphTitle
          title={`About | Two Maids | ${franchisee.name}`}
        />
        <div className='top-background-dots about'></div>

        <OpenGraphDescription>
          {/* Add About description here */}
          {`About Two Maids  ${franchisee.name}`}
        </OpenGraphDescription>

        <Section>
          <>
            {aboutUsData.approved && !aboutUsData.rejected ? (
              <>
                <Row className='justify-content-center'>
                  <Col xs={12} md={12} lg={12} className='mb-5'>
                    <div className='about-us-top-content'>
                      <h1>About Us</h1>
                      <h5>Two Maids prioritizes customer service and quality</h5>
                      <p>
                        At Two Maids, our goal is to make a difference in people's daily lives. We help busy families maintain beautiful, 
                        clean and healthy homes and take care of the chores that no one has the time or energy to do. We proudly serve our 
                        customers so they can focus on what matters most to them.
                      </p>
                      <p>
                        Two Maids actively supports the "<a href='https://cleaningforareason.org/' target='_blank'>Cleaning for a Reason</a>" 
                        organization, which offers free housecleaning to people undergoing cancer treatment. Most of us have experienced cancer 
                        first-hand and we know that the kindness and service of others is a blessing that can provide some comfort and peace of 
                        mind in challenging times. Two Maids is proud to play a small part in that effort. We also partner with <a href='https://www.allianceforhope.com/' target='_blank'>Alliance for Hope International</a> to help provide support and care for survivors of domestic violence.
                      </p>
                      <p>
                        At Two Maids, we endeavor to build dedicated and honest teams that looks forward to caring for your home. We know that 
                        providing our Professional House Cleaners with a growth plan, healthy compensation, and an environment that fosters 
                        encouragement and compassion will result in happier employees that can better meet or exceed our customers expectations.
                      </p>
                
                      <p>
                        Two Maids cherishes the value people with disabilities bring to all aspects of life and we strive to ensure access to 
                        employment opportunities and our services where possible. We work diligently to align our websites and web applications 
                        with the latest Web Content Accessibility Guidelines (WCAG). We work to keep pace with these ever-evolving standards because 
                        we recognize their importance in making the digital world a more accessible environment for those with disabilities. <a href="/accessibility">View Accessibility Statement</a>.
                      </p>
                    </div>

                    <div className='about-us-franchisee-content'>
                      <div className='about-us-text'>
                        <h5>Our {franchisee.name} Team</h5>
                        {aboutUsData.content
                          .split('\n')
                          .map((paragraph, index) => (
                            <p key={index} className='texty-justify'>
                              {paragraph}
                            </p>
                          ))}
                      </div>
                      <div className='about-us-photo-container'>
                        <img
                          src={`${
                            aboutUsData.photoLink
                              ? `https://res.cloudinary.com/twomaidsengi/image/upload/w_auto,f_auto,q_100/${aboutUsData.photoLink}`
                              : `https://res.cloudinary.com/twomaidsengi/image/upload/v1688571228/placeholder-person_vapvjq.jpg`
                          }`}
                          alt={`Owner | Two Maids ${franchisee.name}`}
                        />
                        <div className='about-us-circle'>
                          <p>{aboutUsData.content.split('.')[0]}.</p>
                          <p style={{ fontWeight: 'bold' }}>
                            {franchisee.city}, {franchisee.state}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {contactUsData?.approved && !contactUsData?.rejected && (
                  <>
                    <Section>
                      <PageTitle className=''>
                        Get to Know Your Two Maids {' '}
                        {/* {data.franchiseJson.primaryArea}  */}
                        {franchisee.name} Manager, {contactUsData.managerName}
                      </PageTitle>
                      {/* Add about page content Here */}
                      {contactUsData?.photoLink && <picture id='about-img' className='float-left mb-3 pr-4'>
                        <source
                          media='(min-width: 1200px)'
                          srcSet={`https://res.cloudinary.com/twomaidsengi/image/upload/f_auto,q_100,w_355/${contactUsData.photoLink}`}
                        />
                        <source
                          media='(min-width: 992px)'
                          srcSet={`https://res.cloudinary.com/twomaidsengi/image/upload/f_auto,q_100,w_255/${contactUsData.photoLink}`}
                        />
                        <source
                          media='(min-width: 768px)'
                          srcSet={`https://res.cloudinary.com/twomaidsengi/image/upload/f_auto,q_100,w_720/${contactUsData.photoLink}`}
                        />
                        <source
                          media='(min-width: 576px)'
                          srcSet={`https://res.cloudinary.com/twomaidsengi/image/upload/f_auto,q_100,w_576/${contactUsData.photoLink}`}
                        />
                        <source
                          srcSet={`https://res.cloudinary.com/twomaidsengi/image/upload/f_auto,q_100,w_576/${contactUsData.photoLink}`}
                        />

                        <img
                          src={`https://res.cloudinary.com/twomaidsengi/image/upload/w_auto,f_auto,q_100/${contactUsData.photoLink}`}
                          alt={`${contactUsData.managerName} | Manager at Two Maids ${franchisee.name}`}
                        />
                      </picture>}
                      <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        <span style={{ textDecoration: 'underline' }}>
                          Where are you from?
                        </span>
                        <br />
                        {contactUsData.answer1}
                      </p>
                      <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        <span style={{ textDecoration: 'underline' }}>
                          What's the main reason you work here?
                        </span>
                        <br />
                        {contactUsData.answer2}
                      </p>
                      <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        <span style={{ textDecoration: 'underline' }}>
                          If you don't work here, what would you do instead?
                        </span>
                        <br />
                        {contactUsData.answer3}
                      </p>
                      <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        <span style={{ textDecoration: 'underline' }}>
                          What's your philosophy on customer service?
                        </span>
                        <br />
                        {contactUsData.answer4}
                      </p>
                      <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        <span style={{ textDecoration: 'underline' }}>
                          What are you most proud of?
                        </span>
                        <br />
                        {contactUsData.answer5}
                      </p>
                    </Section>
                  </>
                )}
              </>
            ) : (
              <AboutUsDefaultContent />
            )}
          </>
          {/* )} */}
        </Section>
      </Layout>
    </FranchiseeProvider>
  );
};

About.defaultProps = {
  aboutUsData: null,
  contactUsData: null,
};

export default About;
